import { useState } from "react";
import { CheckCircle2, Loader2, X } from "lucide-react";
import {
  submitLead,
  type LeadRequestType,
} from "../services/lead-capture-api";

type LeadCaptureModalProps = {
  open: boolean;
  onClose: () => void;
  requestType: LeadRequestType;
  title: string;
  description: string;
};

const initialFormState = {
  fullName: "",
  workEmail: "",
  companyName: "",
  phone: "",
  message: "",
  website: "", // honeypot
};

export default function LeadCaptureModal({
  open,
  onClose,
  requestType,
  title,
  description,
}: LeadCaptureModalProps) {
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function update<K extends keyof typeof initialFormState>(
    key: K,
    value: string,
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleClose() {
    setForm(initialFormState);
    setSubmitted(false);
    setError(null);
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.fullName.trim() || !form.workEmail.trim() || !form.companyName.trim()) {
      setError("Please fill in your name, work email, and company.");
      return;
    }

    setSubmitting(true);
    try {
      await submitLead({
        requestType,
        fullName: form.fullName.trim(),
        workEmail: form.workEmail.trim(),
        companyName: form.companyName.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        website: form.website,
      });
      setSubmitted(true);
    } catch {
      setError(
        "Could not send your request right now. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4 py-8 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0b1220] p-6 shadow-2xl">
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-4 top-4 text-slate-400 transition-colors hover:text-white"
        >
          <X size={18} />
        </button>

        {submitted ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="text-emerald-300" size={40} />
            <h2 className="text-lg font-semibold text-white">Request sent</h2>
            <p className="text-sm leading-6 text-slate-400">
              Thanks — our team will reach out to you shortly at{" "}
              <span className="text-slate-200">{form.workEmail}</span>.
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-2 rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-400"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            <p className="mt-1 text-sm leading-6 text-slate-400">
              {description}
            </p>

            {error && (
              <div className="mt-4 rounded-lg border border-red-700/50 bg-red-900/30 px-4 py-2.5 text-sm text-red-300">
                {error}
              </div>
            )}

            <form onSubmit={(e) => void handleSubmit(e)} className="mt-5 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-300">
                  Full name
                </label>
                <input
                  type="text"
                  required
                  value={form.fullName}
                  onChange={(e) => update("fullName", e.target.value)}
                  className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="Jane Operator"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-300">
                  Work email
                </label>
                <input
                  type="email"
                  required
                  value={form.workEmail}
                  onChange={(e) => update("workEmail", e.target.value)}
                  className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="jane@company.com"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-300">
                  Company
                </label>
                <input
                  type="text"
                  required
                  value={form.companyName}
                  onChange={(e) => update("companyName", e.target.value)}
                  className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="Example Freight Ltd"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-300">
                  Phone <span className="text-slate-500">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="+44 7000 000000"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-300">
                  Message <span className="text-slate-500">(optional)</span>
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={3}
                  className="w-full resize-none rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="Anything specific you'd like us to know?"
                />
              </div>

              {/* Honeypot field — hidden from real users, catches simple bots */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                className="hidden"
                aria-hidden="true"
              />

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-sky-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting && <Loader2 className="animate-spin" size={16} />}
                {submitting ? "Sending..." : "Send request"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
